import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, UnauthorizedError } from '@/api/client';
import { identityApi } from '@/api';
import { queryKeys } from '@/api/queryKeys';
import { clearStoredToken, getStoredToken, setStoredToken } from './tokenProvider';
import { AuthContext, type AuthContextValue } from './AuthContext';

interface LoginResponse {
  access_token: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const queryClient = useQueryClient();
  const {
    data: currentUser,
    isLoading: isProfileLoading,
    error: profileError,
  } = useQuery({
    queryKey: [...queryKeys.me, token],
    queryFn: identityApi.getMe,
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (profileError instanceof UnauthorizedError) {
      clearStoredToken();
      setToken(null);
      queryClient.removeQueries({ queryKey: queryKeys.me });
    }
  }, [profileError, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(token) && !(profileError instanceof UnauthorizedError),
      isInitializing: false,
      currentUser,
      isProfileLoading: Boolean(token) && isProfileLoading,
      profileError,
      role: currentUser?.role ?? null,
      login: async (identifier, password) => {
        const { data } = await apiClient.post<LoginResponse>('/auth/login', {
          identifier,
          password,
          client: 'backoffice',
        });
        setStoredToken(data.access_token);
        queryClient.removeQueries({ queryKey: queryKeys.me });
        setToken(data.access_token);
      },
      logout: () => {
        const activeToken = getStoredToken();
        clearStoredToken();
        queryClient.clear();
        setToken(null);
        if (activeToken) {
          void apiClient
            .post('/auth/logout', undefined, {
              headers: { Authorization: `Bearer ${activeToken}` },
            })
            .catch(() => undefined);
        }
      },
    }),
    [token, currentUser, isProfileLoading, profileError, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
